import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './TalentProfiles.css';

const TalentProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTalentId, setSelectedTalentId] = useState('');
  const [editingProfile, setEditingProfile] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state for adding/editing profiles
  const [formData, setFormData] = useState({
    talentId: '',
    clientMsg: '',
    availMap: '{}',
    behavioralStatuses: '{}',
    skillsStatuses: '{}',
    starStatuses: '{}',
    secondStatuses: '{}'
  });

  // Fetch profiles on component mount
  useEffect(() => {
    // We'll need to get talent IDs first, then fetch profiles
    fetchTalentsForProfiles();
  }, []);

  // Fetch talents to get available talent IDs
  const fetchTalentsForProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      // eslint-disable-next-line no-unused-vars
      const response = await apiService.getTalents();
      // For now, we'll work with individual profile fetching
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch a specific talent profile
  const fetchTalentProfile = async (talentId) => {
    if (!talentId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getTalentProfile(talentId);
      if (response.ok && response.row) {
        setProfiles(prev => {
          const existing = prev.find(p => p.talentId === talentId);
          if (existing) {
            return prev.map(p => p.talentId === talentId ? response.row : p);
          } else {
            return [...prev, response.row];
          }
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching talent profile:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle add profile
  const handleAddProfile = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateTalentProfile(formData);
      setFormData({
        talentId: '',
        clientMsg: '',
        availMap: '{}',
        behavioralStatuses: '{}',
        skillsStatuses: '{}',
        starStatuses: '{}',
        secondStatuses: '{}'
      });
      setShowAddForm(false);
      fetchTalentProfile(formData.talentId);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle edit profile
  const handleEditProfile = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateTalentProfile(formData);
      setEditingProfile(null);
      setFormData({
        talentId: '',
        clientMsg: '',
        availMap: '{}',
        behavioralStatuses: '{}',
        skillsStatuses: '{}',
        starStatuses: '{}',
        secondStatuses: '{}'
      });
      fetchTalentProfile(formData.talentId);
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing a profile
  const startEdit = (profile) => {
    setEditingProfile(profile.talentId);
    setFormData({
      talentId: profile.talentId || '',
      clientMsg: profile.clientMsg || '',
      availMap: profile.availMap || '{}',
      behavioralStatuses: profile.behavioralStatuses || '{}',
      skillsStatuses: profile.skillsStatuses || '{}',
      starStatuses: profile.starStatuses || '{}',
      secondStatuses: profile.secondStatuses || '{}'
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingProfile(null);
    setFormData({
      talentId: '',
      clientMsg: '',
      availMap: '{}',
      behavioralStatuses: '{}',
      skillsStatuses: '{}',
      starStatuses: '{}',
      secondStatuses: '{}'
    });
  };

  // Handle fetch profile by talent ID
  const handleFetchProfile = (e) => {
    e.preventDefault();
    if (selectedTalentId) {
      fetchTalentProfile(selectedTalentId);
    }
  };

  // Parse JSON safely
  const parseJsonSafely = (jsonString) => {
    try {
      return JSON.parse(jsonString || '{}');
    } catch {
      return {};
    }
  };

  if (loading) {
    return <div className="loading">Loading talent profiles...</div>;
  }

  return (
    <div className="profiles-container">
      <div className="profiles-header">
        <h2>Talent Profiles Management</h2>
        
        {/* Fetch Profile Form */}
        <form onSubmit={handleFetchProfile} className="fetch-form">
          <input
            type="text"
            value={selectedTalentId}
            onChange={(e) => setSelectedTalentId(e.target.value)}
            placeholder="Enter Talent ID to fetch profile"
            className="fetch-input"
            required
          />
          <button type="submit" className="fetch-btn">Fetch Profile</button>
        </form>

        {/* Add Profile Button */}
        <button 
          onClick={() => setShowAddForm(true)}
          className="add-btn"
        >
          Add New Profile
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingProfile) && (
        <div className="form-modal">
          <div className="form-content">
            <h3>{editingProfile ? 'Edit Profile' : 'Add New Profile'}</h3>
            <form onSubmit={editingProfile ? handleEditProfile : handleAddProfile}>
              <div className="form-group">
                <label>Talent ID</label>
                <input
                  type="text"
                  name="talentId"
                  value={formData.talentId}
                  onChange={handleInputChange}
                  placeholder="Talent ID"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Client Message</label>
                <textarea
                  name="clientMsg"
                  value={formData.clientMsg}
                  onChange={handleInputChange}
                  placeholder="Client message"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Availability Map (JSON)</label>
                <textarea
                  name="availMap"
                  value={formData.availMap}
                  onChange={handleInputChange}
                  placeholder='{"monday": "available", "tuesday": "busy"}'
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Behavioral Statuses (JSON)</label>
                <textarea
                  name="behavioralStatuses"
                  value={formData.behavioralStatuses}
                  onChange={handleInputChange}
                  placeholder='{"communication": "excellent", "teamwork": "good"}'
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Skills Statuses (JSON)</label>
                <textarea
                  name="skillsStatuses"
                  value={formData.skillsStatuses}
                  onChange={handleInputChange}
                  placeholder='{"javascript": "expert", "react": "advanced"}'
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Star Statuses (JSON)</label>
                <textarea
                  name="starStatuses"
                  value={formData.starStatuses}
                  onChange={handleInputChange}
                  placeholder='{"rating": 5, "reviews": 10}'
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Second Statuses (JSON)</label>
                <textarea
                  name="secondStatuses"
                  value={formData.secondStatuses}
                  onChange={handleInputChange}
                  placeholder='{"additional": "data"}'
                  rows="3"
                />
              </div>
              
              <div className="form-actions">
                <button type="submit" className="save-btn">
                  {editingProfile ? 'Update' : 'Add'} Profile
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAddForm(false);
                    cancelEdit();
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profiles List */}
      <div className="profiles-list">
        {profiles.length === 0 ? (
          <div className="no-data">No profiles found. Enter a Talent ID to fetch a profile.</div>
        ) : (
          <div className="profiles-grid">
            {profiles.map((profile) => (
              <div key={profile.talentId} className="profile-card">
                <div className="profile-header">
                  <h4>Profile: {profile.talentId}</h4>
                  <div className="profile-actions">
                    <button 
                      onClick={() => startEdit(profile)}
                      className="edit-btn"
                    >
                      Edit
                    </button>
                  </div>
                </div>
                
                <div className="profile-details">
                  <div className="detail-section">
                    <h5>Client Message</h5>
                    <p>{profile.clientMsg || 'No message'}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h5>Availability Map</h5>
                    <pre className="json-display">
                      {JSON.stringify(parseJsonSafely(profile.availMap), null, 2)}
                    </pre>
                  </div>
                  
                  <div className="detail-section">
                    <h5>Behavioral Statuses</h5>
                    <pre className="json-display">
                      {JSON.stringify(parseJsonSafely(profile.behavioralStatuses), null, 2)}
                    </pre>
                  </div>
                  
                  <div className="detail-section">
                    <h5>Skills Statuses</h5>
                    <pre className="json-display">
                      {JSON.stringify(parseJsonSafely(profile.skillsStatuses), null, 2)}
                    </pre>
                  </div>
                  
                  <div className="detail-section">
                    <h5>Star Statuses</h5>
                    <pre className="json-display">
                      {JSON.stringify(parseJsonSafely(profile.starStatuses), null, 2)}
                    </pre>
                  </div>
                  
                  <div className="detail-section">
                    <h5>Second Statuses</h5>
                    <pre className="json-display">
                      {JSON.stringify(parseJsonSafely(profile.secondStatuses), null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentProfiles;
